import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { Plus, Search, Filter, Trash2, CalendarIcon, Phone, Users, Mail, FileText, Send, Building2, Target, Check, ChevronsUpDown, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  { value: 'Call', icon: Phone, color: '#007AFF', bgColor: '#EFF6FF' },
  { value: 'Meeting', icon: Users, color: '#10B981', bgColor: '#ECFDF5' },
  { value: 'Email', icon: Mail, color: '#F59E0B', bgColor: '#FFFBEB' },
  { value: 'Proposal Sent', icon: FileText, color: '#8B5CF6', bgColor: '#F5F3FF' },
  { value: 'Contract Sent', icon: Send, color: '#FF3B30', bgColor: '#FEF2F2' }
];

const ActivitiesPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [linkType, setLinkType] = useState('opportunity'); // 'opportunity' or 'account'
  
  const [formData, setFormData] = useState({
    opportunity_id: '',
    account_id: '',
    owner_id: '',
    activity_type: '',
    date: '',
    notes: '',
    contact_name: '',
    contact_role: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [activitiesRes, oppsRes, accountsRes, usersRes] = await Promise.all([
        axios.get(`${API}/activities`, { headers }),
        axios.get(`${API}/opportunities`, { headers }),
        axios.get(`${API}/accounts`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);
      setActivities(activitiesRes.data);
      setOpportunities(oppsRes.data);
      setAccounts(accountsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation: require contact when linking to account
    if (linkType === 'account' && formData.account_id && !formData.contact_name) {
      toast.error('Contact name is required when logging an activity to an account');
      return;
    }
    
    try {
      const payload = {
        owner_id: formData.owner_id,
        activity_type: formData.activity_type,
        notes: formData.notes,
        contact_name: formData.contact_name || null,
        contact_role: formData.contact_role || null,
        date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
      };
      
      // Only include the relevant ID based on link type
      if (linkType === 'opportunity' && formData.opportunity_id) {
        payload.opportunity_id = formData.opportunity_id;
      } else if (linkType === 'account' && formData.account_id) {
        payload.account_id = formData.account_id;
      }
      
      await axios.post(`${API}/activities`, payload, {
        headers: getAuthHeaders()
      });
      toast.success('Activity logged successfully');
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save activity:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save activity';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      await axios.delete(`${API}/activities/${activityId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Activity deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete activity:', error);
      toast.error('Failed to delete activity');
    }
  };

  const resetForm = () => {
    setFormData({
      opportunity_id: '',
      account_id: '',
      owner_id: user?.id || '',
      activity_type: '',
      date: '',
      notes: '',
      contact_name: '',
      contact_role: ''
    });
    setSelectedDate(new Date());
    setLinkType('opportunity');
    setIsCreateOpen(false);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const getActivityIcon = (type) => {
    const activity = ACTIVITY_TYPES.find(a => a.value === type);
    return activity || ACTIVITY_TYPES[0];
  };

  // Get display name for activity (from opportunity or account)
  const getActivityTarget = (activity) => {
    if (activity.opportunity) {
      return {
        name: activity.opportunity?.account?.university_name || 'Unknown',
        subtext: activity.opportunity?.sport,
        type: 'opportunity'
      };
    } else if (activity.account) {
      return {
        name: activity.account?.university_name || 'Unknown',
        subtext: 'No opportunity yet',
        type: 'account'
      };
    }
    return { name: 'Unknown', subtext: '', type: 'unknown' };
  };

  const filteredActivities = activities.filter(activity => {
    const target = getActivityTarget(activity);
    const matchesSearch = target.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || activity.activity_type === filterType;
    const matchesOwner = filterOwner === 'all' || activity.owner_id === filterOwner;
    return matchesSearch && matchesType && matchesOwner;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            ACTIVITIES
          </h1>
          <p className="text-[#475569] mt-1">Track Sales Engagement & Motion</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateOpen(open); }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold uppercase tracking-wide shadow-lg hover:shadow-xl transition-all"
              data-testid="create-activity-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                LOG ACTIVITY
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Link Type Toggle */}
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Link To</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={linkType === 'opportunity' ? 'default' : 'outline'}
                    className={linkType === 'opportunity' 
                      ? 'flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white' 
                      : 'flex-1 border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]'}
                    onClick={() => {
                      setLinkType('opportunity');
                      setFormData({ ...formData, account_id: '' });
                    }}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Opportunity
                  </Button>
                  <Button
                    type="button"
                    variant={linkType === 'account' ? 'default' : 'outline'}
                    className={linkType === 'account' 
                      ? 'flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white' 
                      : 'flex-1 border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]'}
                    onClick={() => {
                      setLinkType('account');
                      setFormData({ ...formData, opportunity_id: '' });
                    }}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Account Only
                  </Button>
                </div>
                <p className="text-xs text-[#94A3B8]">
                  {linkType === 'account' 
                    ? 'For activities before an opportunity exists' 
                    : 'Link to an existing opportunity'}
                </p>
              </div>

              {/* Opportunity or Account Selection */}
              {linkType === 'opportunity' ? (
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Opportunity</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]"
                        data-testid="activity-opportunity-select"
                      >
                        {formData.opportunity_id
                          ? (() => {
                              const opp = opportunities.find(o => o.id === formData.opportunity_id);
                              return opp ? `${opp.account?.university_name} - ${opp.sport}` : 'Select opportunity...';
                            })()
                          : "Search opportunities..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0 bg-white border-[#E2E8F0]">
                      <Command>
                        <CommandInput placeholder="Search opportunity..." className="h-9" />
                        <CommandList>
                          <CommandEmpty>No opportunity found.</CommandEmpty>
                          <CommandGroup>
                            {opportunities.map((opp) => (
                              <CommandItem
                                key={opp.id}
                                value={`${opp.account?.university_name} ${opp.sport}`}
                                onSelect={() => {
                                  setFormData({ ...formData, opportunity_id: opp.id });
                                }}
                                className="cursor-pointer"
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${formData.opportunity_id === opp.id ? "opacity-100" : "opacity-0"}`}
                                />
                                <div>
                                  <p>{opp.account?.university_name}</p>
                                  <p className="text-xs text-[#94A3B8]">{opp.sport}</p>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Account (University)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]"
                        data-testid="activity-account-select"
                      >
                        {formData.account_id
                          ? accounts.find(a => a.id === formData.account_id)?.university_name
                          : "Search accounts..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0 bg-white border-[#E2E8F0]">
                      <Command>
                        <CommandInput placeholder="Search university..." className="h-9" />
                        <CommandList>
                          <CommandEmpty>No account found.</CommandEmpty>
                          <CommandGroup>
                            {accounts.map((account) => (
                              <CommandItem
                                key={account.id}
                                value={account.university_name}
                                onSelect={() => {
                                  setFormData({ ...formData, account_id: account.id });
                                }}
                                className="cursor-pointer"
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${formData.account_id === account.id ? "opacity-100" : "opacity-0"}`}
                                />
                                {account.university_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Activity Type</Label>
                <Select
                  value={formData.activity_type}
                  onValueChange={(value) => setFormData({ ...formData, activity_type: value })}
                >
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="activity-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0]">
                    {ACTIVITY_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-[#0F172A] hover:bg-[#F1F5F9]">
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" style={{ color: type.color }} />
                          {type.value}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Owner</Label>
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="activity-owner-select">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0]">
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id} className="text-[#0F172A] hover:bg-[#F1F5F9]">
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9]"
                        data-testid="activity-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PP') : 'Pick date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-[#E2E8F0]">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        initialFocus
                        className="bg-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              {/* Contact Information */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Contact Person (Coach/AD)</Label>
                  {linkType === 'account' && (
                    <Badge className="bg-[#FEF3C7] text-[#D97706] text-[10px] border-0">Required</Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Contact name"
                    className={`bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] ${linkType === 'account' ? 'ring-1 ring-[#F59E0B]' : ''}`}
                    data-testid="activity-contact-name"
                    required={linkType === 'account'}
                  />
                  <Input
                    value={formData.contact_role}
                    onChange={(e) => setFormData({ ...formData, contact_role: e.target.value })}
                    placeholder="Role (e.g., Head Coach, AD)"
                    className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                    data-testid="activity-contact-role"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#475569] text-sm uppercase tracking-wider font-medium">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Activity notes..."
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] min-h-[100px]"
                  data-testid="activity-notes-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1 border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-bold"
                  data-testid="activity-submit-btn"
                >
                  Log Activity
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ACTIVITY_TYPES.map(type => {
          const count = activities.filter(a => a.activity_type === type.value).length;
          const Icon = type.icon;
          return (
            <Card key={type.value} className="bg-white border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: type.bgColor }}
                >
                  <Icon className="w-5 h-5" style={{ color: type.color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {count}
                  </p>
                  <p className="text-xs text-[#94A3B8]">{type.value}s</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search activities..."
                className="pl-10 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]"
                data-testid="search-activities-input"
              />
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-type-select">
                <SelectValue placeholder="Activity Type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Types</SelectItem>
                {ACTIVITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value} className="text-[#0F172A]">{type.value}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterOwner} onValueChange={setFilterOwner}>
              <SelectTrigger className="w-full md:w-44 bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A]" data-testid="filter-owner-select">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A]">All Owners</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id} className="text-[#0F172A]">{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activities Table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0] hover:bg-transparent bg-[#F8FAFC]">
                <TableHead className="text-[#475569] font-semibold">Type</TableHead>
                <TableHead className="text-[#475569] font-semibold">Account / Opportunity</TableHead>
                <TableHead className="text-[#475569] font-semibold">Contact</TableHead>
                <TableHead className="text-[#475569] font-semibold">Owner</TableHead>
                <TableHead className="text-[#475569] font-semibold">Date</TableHead>
                <TableHead className="text-[#475569] font-semibold">Notes</TableHead>
                <TableHead className="text-[#475569] font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredActivities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#94A3B8]">
                    No activities found
                  </TableCell>
                </TableRow>
              ) : (
                filteredActivities.map((activity) => {
                  const activityType = getActivityIcon(activity.activity_type);
                  const Icon = activityType.icon;
                  const target = getActivityTarget(activity);
                  return (
                    <TableRow 
                      key={activity.id} 
                      className="border-[#E2E8F0] hover:bg-[#F8FAFC]"
                      data-testid={`activity-row-${activity.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-8 h-8 rounded-md flex items-center justify-center"
                            style={{ backgroundColor: activityType.bgColor }}
                          >
                            <Icon className="w-4 h-4" style={{ color: activityType.color }} />
                          </div>
                          <span className="text-[#0F172A] font-medium">{activity.activity_type}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-[#0F172A] font-medium">{target.name}</p>
                            <div className="flex items-center gap-1">
                              <p className="text-xs text-[#94A3B8]">{target.subtext}</p>
                              {target.type === 'account' && (
                                <Badge className="bg-[#FEF3C7] text-[#D97706] text-[10px] px-1 py-0 border-0">
                                  PRE-OPP
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {activity.contact_name ? (
                          <div>
                            <p className="text-[#0F172A] font-medium text-sm">{activity.contact_name}</p>
                            {activity.contact_role && (
                              <p className="text-xs text-[#94A3B8]">{activity.contact_role}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#94A3B8] text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 border border-[#E2E8F0]">
                            <AvatarImage src={activity.owner?.avatar_url} />
                            <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-xs">
                              {getInitials(activity.owner?.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-[#475569] text-sm">{activity.owner?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#475569]">
                        {activity.date}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {activity.notes ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 cursor-pointer">
                                  <MessageSquare className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                                  <p className="text-[#475569] text-sm truncate max-w-[200px]">{activity.notes}</p>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-md bg-white border border-[#E2E8F0] shadow-lg p-3">
                                <p className="text-[#0F172A] text-sm whitespace-pre-wrap">{activity.notes}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-[#94A3B8] text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(activity.id)}
                          className="text-[#475569] hover:text-[#FF3B30] hover:bg-[#FEF2F2]"
                          data-testid={`delete-activity-${activity.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-[#94A3B8]">
        <span>Showing {filteredActivities.length} of {activities.length} activities</span>
      </div>
    </div>
  );
};

export default ActivitiesPage;
